import { useMemo, useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/lib/api";
import { Plus, Calendar as CalIcon, Trash2, Link2, Link2Off, X, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

function formatDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}

export default function CalendarPanel({ family }) {
  const qc = useQueryClient();
  const { data: events = [] } = useQuery({
    queryKey: ["events"],
    queryFn: endpoints.events,
    refetchInterval: 1000 * 60 * 5,
  });
  const { data: gStatus } = useQuery({
    queryKey: ["calendar-status"],
    queryFn: endpoints.calendarStatus,
    refetchInterval: 1000 * 60,
  });

  // Handle OAuth return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("google_connected")) {
      toast.success("Google Calendar connected");
      qc.invalidateQueries({ queryKey: ["calendar-status"] });
      qc.invalidateQueries({ queryKey: ["events"] });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (params.get("google_error")) {
      toast.error("Could not connect Google Calendar");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [qc]);

  const [connectOpen, setConnectOpen] = useState(false);
  const [authUrl, setAuthUrl] = useState(null);

  const connectMut = useMutation({
    mutationFn: endpoints.calendarLoginUrl,
    onSuccess: (data) => {
      setAuthUrl(data.authorization_url);
      setConnectOpen(true);
    },
    onError: () => toast.error("Could not start Google connection"),
  });

  // While the QR dialog is open, poll status every 2s. When the phone completes
  // the OAuth flow, the backend will have stored the tokens and this poll picks it up.
  const { data: gStatusPolled } = useQuery({
    queryKey: ["calendar-status-connecting"],
    queryFn: endpoints.calendarStatus,
    enabled: connectOpen,
    refetchInterval: 2000,
  });

  useEffect(() => {
    if (connectOpen && gStatusPolled?.connected) {
      setConnectOpen(false);
      setAuthUrl(null);
      toast.success(`Google Calendar connected${gStatusPolled.email ? " (" + gStatusPolled.email + ")" : ""}`);
      qc.invalidateQueries({ queryKey: ["calendar-status"] });
      qc.invalidateQueries({ queryKey: ["events"] });
    }
  }, [connectOpen, gStatusPolled, qc]);

  const disconnectMut = useMutation({
    mutationFn: endpoints.calendarDisconnect,
    onSuccess: () => {
      toast.success("Disconnected from Google");
      qc.invalidateQueries({ queryKey: ["calendar-status"] });
      qc.invalidateQueries({ queryKey: ["events"] });
    },
  });

  const familyById = useMemo(
    () => Object.fromEntries(family.map((f) => [f.id, f])),
    [family]
  );

  const sorted = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return [...events]
      .filter((e) => e.date >= today)
      .sort((a, b) => (a.date + (a.time || "")).localeCompare(b.date + (b.time || "")));
  }, [events]);

  // Mini month grid
  const [cursor] = useState(new Date());
  const monthGrid = useMemo(() => buildMonth(cursor), [cursor]);
  const eventsByDate = useMemo(() => {
    const m = {};
    for (const e of events) {
      (m[e.date] ||= []).push(e);
    }
    return m;
  }, [events]);

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [time, setTime] = useState("");
  const [assigneeId, setAssigneeId] = useState("none");

  const createMut = useMutation({
    mutationFn: endpoints.createEvent,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["events"] });
      toast.success("Event added");
      setOpen(false);
      setTitle("");
      setTime("");
      setAssigneeId("none");
    },
  });

  const delMut = useMutation({
    mutationFn: endpoints.deleteEvent,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }),
  });

  function submit() {
    if (!title.trim()) return toast.error("Title required");
    createMut.mutate({
      title: title.trim(),
      date,
      time: time || null,
      assignee_id: assigneeId === "none" ? null : assigneeId,
    });
  }

  return (
    <div
      data-testid="calendar-card"
      className="wood-panel rounded-[2rem] border p-6 lg:p-8 flex flex-col overflow-hidden flex-1 min-h-0"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3 min-w-0">
          <CalIcon size={22} className="text-[#D6A85B] shrink-0" />
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight text-[#EAE4DB] truncate">
            {cursor.toLocaleDateString([], { month: "long", year: "numeric" })}
          </h2>
          {gStatus?.connected && (
            <span
              data-testid="google-connected-badge"
              className="hidden md:inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#2D2A28] text-[0.65rem] font-mono uppercase tracking-[0.15em] text-[#6B8E7B] border border-[#33302C]"
              title={gStatus.email}
            >
              <Link2 size={11} /> Google
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {gStatus?.configured && !gStatus?.connected && (
            <button
              data-testid="connect-google-btn"
              onClick={() => connectMut.mutate()}
              disabled={connectMut.isPending}
              className="h-11 px-4 rounded-full bg-[#6B8E7B] text-[#171514] flex items-center gap-2 hover:opacity-90 transition font-semibold text-sm uppercase tracking-wider"
            >
              <Link2 size={16} /> <span>Connect Google</span>
            </button>
          )}
          {gStatus?.connected && (
            <button
              data-testid="disconnect-google-btn"
              onClick={() => disconnectMut.mutate()}
              className="h-11 w-11 rounded-full bg-[#2D2A28] border border-[#33302C] text-[#A6A098] hover:text-[#C85A5A] flex items-center justify-center transition"
              title="Disconnect Google"
              aria-label="Disconnect Google"
            >
              <Link2Off size={16} />
            </button>
          )}

          {/* Google OAuth via QR code — user scans with phone, completes login there */}
          <Dialog
            open={connectOpen}
            onOpenChange={(v) => {
              setConnectOpen(v);
              if (!v) setAuthUrl(null);
            }}
          >
            <DialogContent
              data-testid="google-qr-dialog"
              className="bg-[#23211F] border-[#33302C] text-[#EAE4DB] max-w-md"
            >
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl flex items-center gap-2">
                  <Smartphone size={20} className="text-[#6B8E7B]" /> Sign in with your phone
                </DialogTitle>
                <DialogDescription className="text-[#A6A098] leading-relaxed">
                  Scan this code with your phone&apos;s camera. Complete the Google
                  sign-in on your phone — Hearth will connect automatically.
                </DialogDescription>
              </DialogHeader>

              <div className="flex flex-col items-center py-4 gap-4">
                {authUrl ? (
                  <div className="bg-white p-4 rounded-2xl" data-testid="google-qr-code">
                    <QRCodeSVG
                      value={authUrl}
                      size={240}
                      level="M"
                      includeMargin={false}
                    />
                  </div>
                ) : (
                  <div className="w-[264px] h-[264px] bg-[#2D2A28] rounded-2xl animate-pulse" />
                )}
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#A6A098] text-center">
                  Waiting for phone to sign in…
                </div>
                <div className="flex items-center gap-1.5 text-xs text-[#A6A098]">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#6B8E7B] animate-pulse" />
                  Listening for connection
                </div>
              </div>

              <DialogFooter>
                <Button
                  data-testid="cancel-google-connect"
                  variant="outline"
                  onClick={() => {
                    setConnectOpen(false);
                    setAuthUrl(null);
                  }}
                  className="bg-transparent border-[#33302C] text-[#EAE4DB] hover:bg-[#33302C] hover:text-[#EAE4DB]"
                >
                  <X size={14} className="mr-1.5" /> Cancel
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="add-event-btn"
              className="h-11 px-4 rounded-full bg-[#2D2A28] border border-[#33302C] text-[#EAE4DB] flex items-center gap-2 hover:bg-[#33302C] transition-colors"
            >
              <Plus size={16} /> <span className="text-sm uppercase tracking-wider">Event</span>
            </button>
          </DialogTrigger>
          <DialogContent className="bg-[#23211F] border-[#33302C] text-[#EAE4DB]">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">New event</DialogTitle>
              <DialogDescription className="text-[#A6A098]">
                Add a shared event to the family calendar.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-[#A6A098] uppercase text-xs tracking-widest">Title</Label>
                <Input
                  data-testid="event-title-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="mt-1 bg-[#171514] border-[#33302C] text-[#EAE4DB]"
                  placeholder="Soccer practice"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#A6A098] uppercase text-xs tracking-widest">Date</Label>
                  <Input
                    data-testid="event-date-input"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="mt-1 bg-[#171514] border-[#33302C] text-[#EAE4DB]"
                  />
                </div>
                <div>
                  <Label className="text-[#A6A098] uppercase text-xs tracking-widest">Time</Label>
                  <Input
                    data-testid="event-time-input"
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="mt-1 bg-[#171514] border-[#33302C] text-[#EAE4DB]"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[#A6A098] uppercase text-xs tracking-widest">Who</Label>
                <Select value={assigneeId} onValueChange={setAssigneeId}>
                  <SelectTrigger
                    data-testid="event-assignee-select"
                    className="mt-1 bg-[#171514] border-[#33302C] text-[#EAE4DB]"
                  >
                    <SelectValue placeholder="Anyone" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#23211F] border-[#33302C] text-[#EAE4DB]">
                    <SelectItem value="none">Anyone</SelectItem>
                    {family.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button
                data-testid="event-save-btn"
                onClick={submit}
                disabled={createMut.isPending}
                className="bg-[#D6A85B] text-[#171514] hover:bg-[#c79a4f] font-semibold"
              >
                Save event
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center mb-4 shrink-0">
        {["S", "M", "T", "W", "T", "F", "S"].map((w, i) => (
          <div key={`wk-${i}`} className="text-[0.7rem] font-mono uppercase tracking-[0.15em] text-[#A6A098]">
            {w}
          </div>
        ))}
        {monthGrid.map((cell) => {
          const evs = eventsByDate[cell.iso] || [];
          const isToday = cell.iso === new Date().toISOString().slice(0, 10);
          return (
            <div
              key={cell.iso}
              data-testid={`calendar-cell-${cell.iso}`}
              className={`h-9 md:h-10 rounded-lg flex flex-col items-center justify-center text-sm ${
                cell.inMonth ? "text-[#EAE4DB]" : "text-[#5a544e]"
              } ${isToday ? "bg-[#2D2A28] border border-[#D6A85B]" : ""}`}
            >
              <span className={`leading-none ${isToday ? "font-semibold" : ""}`}>{cell.day}</span>
              <div className="flex gap-0.5 mt-0.5">
                {evs.slice(0, 3).map((e) => (
                  <span
                    key={e.id}
                    className="block w-1 h-1 rounded-full"
                    style={{ backgroundColor: familyById[e.assignee_id]?.color || "#A6A098" }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Upcoming events */}
      <div className="border-t border-[#33302C] pt-4 flex-1 min-h-0 overflow-y-auto no-scrollbar">
        <div className="text-xs uppercase font-mono tracking-[0.15em] text-[#A6A098] mb-3">
          Upcoming
        </div>
        {sorted.length === 0 && (
          <div className="text-[#A6A098] text-sm">No upcoming events.</div>
        )}
        <ul data-testid="upcoming-events-list" className="space-y-2.5">
          {sorted.slice(0, 20).map((e) => {
            const f = familyById[e.assignee_id];
            return (
              <li
                key={e.id}
                data-testid={`event-row-${e.id}`}
                className="flex items-center gap-3 group"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: f?.color || "#A6A098" }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[#EAE4DB] text-base truncate">{e.title}</div>
                  <div className="text-xs font-mono uppercase tracking-[0.15em] text-[#A6A098]">
                    {formatDate(e.date)} {e.time ? `· ${e.time}` : ""} {f ? `· ${f.name}` : ""}
                  </div>
                </div>
                <button
                  data-testid={`delete-event-${e.id}`}
                  onClick={() => delMut.mutate(e.id)}
                  className="h-9 w-9 rounded-full text-[#A6A098]/60 hover:text-[#C85A5A] hover:bg-[#2D2A28] flex items-center justify-center transition"
                  aria-label="Delete event"
                >
                  <Trash2 size={16} />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function buildMonth(d) {
  const year = d.getFullYear();
  const month = d.getMonth();
  const first = new Date(year, month, 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay()); // back to Sunday
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    cells.push({
      day: day.getDate(),
      inMonth: day.getMonth() === month,
      iso: day.toISOString().slice(0, 10),
    });
    if (i >= 34 && day.getMonth() !== month) break;
  }
  return cells;
}
